/**
 * @Author: Li, Yu Jia
 * @Date: 2020-01-13 11:38:24
 * @Last Modified by: Li, Yu Jia
 * @Last Modified time: 2021-07-14 13:38:00
 * @Usage: userimage.logics.func
 */

import { ART_CAN_NOT_USED_MESSAGE, mandatoryMissingWarning } from './constants';
import { ProcessTypes, REDUCE_CANVAS_CONTAINER_SIZE, undoRedoType } from '../utils/constants';
import {
  addArt,
  addTemp,
  cancelSelectArt,
  changeArtColor as changeArtColorAction,
  setImageUI,
  updateElementByStatus,
  updateElementDimension,
} from './../canvas/actions';
import {
  addNewElement,
  adjustImageSizeToFitContainer,
  adjustLogoOriginSizeByLimitation,
  checkUploadLogoDpi,
  getImageLayerIndex,
} from '../canvas/logics';
import {
  categoriesInit,
  recentArtsInit,
  removeSavedArtFromRecent,
  savedArtsCountInit,
  savedArtsListInit,
  sentimentListInit,
  setSavedArtThumbnail,
  setUserImageClone,
  setUserImageDimension,
  sharedArtsInit,
  sloganListInit,
  stockArtByCategory,
  verseListInit,
} from './actions';
import {
  changeLogoColorAsync,
  changeTextColorAsync,
  normalUserImageMatch,
  outsideUserImageMatch,
  recordTextColorAsync,
} from './../imprintarea/logics';
import { changeLogoEditingStatus, hideWaitingCover, showMessageBox, showWaitingCover } from './../main/actions';
import {
  colorSpace,
  elementStatus,
  logoEditingStatusEnum,
  stockArtColorType,
  userImageStep,
  userImageType,
} from './../utils/constants';
import {
  getAllColorsInUse,
  getImageElementClone,
  getInnerOrOuterSlogan,
  getSavedArtDict,
  getSavedArts,
  getSentimentList as getSentimentListSelector,
  getSharedArtDict,
  getTempUserImage,
  getUserImageClone,
  getVerseList as getVerseListSelector,
  onlyOneSentimentCheck,
  onlyOneSloganCheck,
  onlyOneVerseCheck,
} from './selectors';
import { getArtThumbnailUrl, getImageDimension } from './../utils/scene7url';
import { getCanvasDraggingArea, getCanvasImageList, getCurrentCanvasData, getTempArt } from '../canvas/selectors';
import { getChildItemIdFromBomSelectionForLite, getIsLiteParentChildItem } from '../lite/selectors';
import {
  getColorsFromColorizeService,
  getSavedArtCount as getSavedArtCountApi,
  getSavedArtDimension,
  getSavedArtListByPage as getSavedArtListByPageApi,
  getSavedArtPreview as getSavedArtPreviewApi,
  getSentimentList as getSentimentListApi,
  getSharedArtList as getSharedArtListApi,
  getNewSloganList as getSloganListApi,
  getStockArtCategories as getStockArtCategoriesApi,
  getStockArtListByCategory as getStockArtListByCategoryApi,
  getStockArtListBySearch as getStockArtListBySearchApi,
  getVerseList as getVerseListApi,
} from './apis';
import {
  getCurrentHotStampedCheck,
  getCurrentImprintAreaKey,
  getCurrentImprintProcessType,
  getItemId,
  getLogoEditingStatus,
  getSelectedTextColor,
  getSloganMandatoryCheck,
  getStudioItemType,
  getVendorId,
} from './../main/selectors';
import {
  getCurrentImprintFormat,
  getDefaultUserImageColor,
  getHasOneImprintColorOnly,
  getImprintColors,
  getImprintOptionMaxColorCount,
  getSentimentAvailable,
  getVerseAvailable,
} from './../imprintarea/selectors';
import { gotoFirstStep, initLogoFromRecent } from '../uploadimage/logics';

import { GoogleTagManager } from './../utils/aop';
import { changeFromPreviewAction } from './constants';
import { fillColorCmyk } from '../uploadimage/components/LogoColorPicker/logics';
import { getIsNextGenUI } from '../nextgen/selectors';
import { keepElementInCanvas } from './../elements/components/BaseElement/logics';
import { layeringType } from '../nextgen/constants';
import { pushToUndoList } from '../canvas/components/UndoRedo/logic';
import scrollIntoView from 'scroll-into-view-if-needed';
import { studioItemType } from './../main/constants';
import { updateFullPreview } from '../preview/logics';
import { v4 as uuidv4 } from 'uuid';

const getSloganList = (imprintAreaCode, vendorId) => (dispatch) =>
  getSloganListApi(imprintAreaCode, vendorId)
    .then((response) => {
      if (response && response.data) {
        const sloganList = response.data.slogans.map((item) => {
          return Object.assign({}, item, {
            guid: uuidv4(),
            thumbnail: getArtThumbnailUrl(item.scene7FileName, item.type),
          });
        });
        const data = {
          slogans: sloganList,
          yearDict: response.data.yearDict,
        };
        dispatch(sloganListInit({ imprintAreaCode, data }));
        dispatch(initMandatorySingleUserImage(userImageType.Slogan));
      }
    })
    .catch((_) => {});

const sortSloganYear = (sortData, sortKey) => {
  const compare = (prop) => {
    return (obj1, obj2) => {
      const val1 = obj1[prop];
      const val2 = obj2[prop];

      return Number(val1) - Number(val2);
    };
  };
  return sortData.sort(compare(sortKey));
};

const getVerseList = (dispatch, getState) => {
  const state = getState();
  const itemId = getItemId(state);
  getVerseListApi(itemId)
    .then((response) => {
      if (response && response.data) {
        const verseList = response.data.map((item) => {
          return Object.assign({}, item, {
            guid: uuidv4(),
            thumbnail: getArtThumbnailUrl(item.scene7FileName, item.type),
          });
        });
        const data = {
          verses: verseList,
        };
        dispatch(verseListInit({ data }));
        dispatch(initMandatorySingleUserImage(userImageType.Verse));
      }
    })
    .catch((_) => {});
};

const getSentimentList = (dispatch, getState) => {
  const state = getState();
  const itemId = getItemId(state);
  getSentimentListApi(itemId)
    .then((response) => {
      if (response && response.data) {
        const sentimentList = response.data.map((item) => {
          return Object.assign({}, item, {
            guid: uuidv4(),
            thumbnail: getArtThumbnailUrl(item.scene7FileName, item.type),
          });
        });
        const data = {
          sentiments: sentimentList,
        };
        dispatch(sentimentListInit({ data }));
        dispatch(initMandatorySingleUserImage(userImageType.Sentiment));
      }
    })
    .catch((_) => {});
};

const getSavedArtPreview = (fileName, processType, imprintAreaCode) =>
  getSavedArtPreviewApi(fileName, processType, imprintAreaCode);

const getStockArtListByCategory = (vendorId, categoryId, callback) => (dispatch, getState) => {
  const state = getState();
  const studioType = getStudioItemType(state);
  const currentImprintFormat = getCurrentImprintFormat(state);
  const maxColorCount =
    studioType === studioItemType.digital || studioType === studioItemType.screenPrintedDigital
      ? 0
      : currentImprintFormat.maxColorCount;
  getStockArtListByCategoryApi(vendorId, categoryId, studioType, maxColorCount)
    .then((response) => {
      if (response && response.data) {
        let list = response.data;
        if (list.length > 0) {
          list = stockArtDataFormat(list);
          dispatch(
            stockArtByCategory({
              imprintAreaCode: currentImprintFormat.imprintAreaCode,
              categoryId: categoryId,
              stockArtList: list,
            })
          );
          callback && typeof callback === 'function' && callback(list);
        }
      }
    })
    .catch((_) => {});
};

const getSavedArtListByPage = (processType, imprintAreaCode, pageIndex, pageSize, callback) => (dispatch, getState) => {
  const savedArtEmptyCheck = (savedArtDict) => {
    const savedKey = `${imprintAreaCode}-${processType}`;
    return !(
      savedArtDict &&
      savedArtDict[savedKey] &&
      savedArtDict[savedKey].list &&
      savedArtDict[savedKey].list[pageIndex] &&
      savedArtDict[savedKey].list[pageIndex].length > 0
    );
  };
  if (savedArtEmptyCheck(getSavedArtDict(getState()))) {
    getSavedArtListByPageApi(processType, imprintAreaCode, pageIndex, pageSize, callback)
      .then((response) => {
        if (response && response.data) {
          let _datalist = response.data;
          if (_datalist.length > 0) {
            _datalist = _datalist.map((saved) => {
              return Object.assign({}, saved, {
                guid: uuidv4(),
              });
            });

            if (savedArtEmptyCheck(getSavedArtDict(getState()))) {
              dispatch(
                savedArtsListInit({
                  processType,
                  imprintAreaCode,
                  pageIndex,
                  list: _datalist,
                })
              );
              callback && typeof callback === 'function' && callback(_datalist);
              if (pageIndex !== 0) {
                dispatch(savedArtPreviewLoadHandler(pageIndex));
              }
            }
          }
        }
      })
      .catch((_) => {});
  }
};

const sharedArtEmptyCheck = (sharedArtDict, currentImprintAreaKey) => {
  const sharedkey = currentImprintAreaKey;
  return !(
    sharedArtDict &&
    sharedArtDict[sharedkey] &&
    sharedArtDict[sharedkey].list &&
    sharedArtDict[sharedkey].list.length > 0
  );
};

const getSharedArtList =
  (vendorId, itemId, processType, currentImprintAreaCode, currentImprintAreaKey, callback) => (dispatch, getState) => {
    if (sharedArtEmptyCheck(getSharedArtDict(getState()), currentImprintAreaKey)) {
      getSharedArtListApi(vendorId, itemId, processType, currentImprintAreaCode)
        .then((response) => {
          if (response && response.data) {
            dispatch(initializeSharedArt(response.data, currentImprintAreaKey, callback));
          }
        })
        .catch((_) => {});
    }
  };

export const initializeSharedArt = (sharedArtList, currentImprintAreaKey, callback) => (dispatch, getState) => {
  if (sharedArtEmptyCheck(getSharedArtDict(getState()), currentImprintAreaKey)) {
    let _datalist = sharedArtList;
    if (_datalist.length > 0) {
      _datalist = _datalist.map((shared) => {
        return Object.assign({}, shared, {
          guid: uuidv4(),
          thumbnail: getArtThumbnailUrl(shared.scene7FileName, shared.type),
        });
      });
      dispatch(
        sharedArtsInit({
          currentImprintAreaKey,
          list: _datalist,
        })
      );
      callback && typeof callback === 'function' && callback();
    }
  }
};

const getSavedArtCount = (processType, imprintAreaCode, callback) => (dispatch, getState) => {
  const savedArtZeroCheck = (savedArtDict) => {
    const savedKey = `${imprintAreaCode}-${processType}`;
    return !(savedArtDict && savedArtDict[savedKey] && savedArtDict[savedKey].count > 0);
  };

  if (savedArtZeroCheck(getSavedArtDict(getState()))) {
    getSavedArtCountApi(processType, imprintAreaCode)
      .then((res) => {
        if (res && res.data) {
          const count = res.data;
          if (count > 0) {
            dispatch(
              savedArtsCountInit({
                processType,
                imprintAreaCode,
                count,
              })
            );

            callback && typeof callback === 'function' && callback(count);
          }
        }
      })
      .catch((_) => {});
  }
};

const stockArtDataFormat = (list) =>
  list.map((item) => {
    let stockId = '';
    if (item.fileName) {
      let fnArr = item.fileName.split('/');
      if (fnArr.length > 0 && fnArr.length === 3) {
        stockId = fnArr[2];
      }
    }
    return Object.assign({}, item, {
      guid: uuidv4(),
      thumbnail: getArtThumbnailUrl(item.scene7FileName, item.type),
      stockId, //for uniqueness in recent list
    });
  });

/**
 *@param StudioItemType - 0:Scene7; 1:Digital; 2:ScreenPrintedDigital
 *@param MaxColorCount: default 0 for Digital product, maxColorCount is only used for Scene7(StudioItemType:0) and ScreenPrintedDigital(StudioItemType:2)
 */
const getStockArtCategories = (vendorId, format) => (dispatch, getState) => {
  const stockArtCategoryEmptyCheck = (stockArtCategories) => {
    return !stockArtCategories[format.imprintAreaCode];
  };

  const state = getState();
  if (stockArtCategoryEmptyCheck(getState().stockArtCategoryDict)) {
    const studioType = getStudioItemType(state);
    const maxColorCount =
      studioType === studioItemType.digital || studioType === studioItemType.screenPrintedDigital
        ? 0
        : format.maxColorCount;
    getStockArtCategoriesApi(vendorId, studioType, maxColorCount)
      .then((response) => {
        if (response && response.data) {
          if (stockArtCategoryEmptyCheck(getState().stockArtCategoryDict)) {
            const categorylist = response.data;
            dispatch(categoriesInit({ imprintAreaCode: format.imprintAreaCode, categorylist }));
          }
        }
      })
      .catch((_) => {});
  }
};

const getStockArtListBySearch =
  ({ vendorId, keywords, resolve, reject }) =>
  (dispatch, getState) => {
    const state = getState();
    const studioType = getStudioItemType(state);
    const currentImprintFormat = getCurrentImprintFormat(state);
    const maxColorCount =
      studioType === studioItemType.digital || studioType === studioItemType.screenPrintedDigital
        ? 0
        : currentImprintFormat.maxColorCount;
    getStockArtListBySearchApi(vendorId, keywords, studioType, maxColorCount)
      .then((response) => {
        if (response && response.data) {
          let list = response.data;
          if (list.length > 0) {
            list = stockArtDataFormat(list);
          }
          resolve && typeof resolve === 'function' && resolve(list);
        }
      })
      .catch((_) => {
        reject && typeof reject === 'function' && reject();
      });
  };
export const recentArtsInitAsync =
  ({ userImage, formatkey }) =>
  (dispatch, getState) => {
    //all type studio: filter out Verse, Sentiment and Slogan
    const state = getState();
    const allowToRecentCollection =
      userImage.type === userImageType.StockArt ||
      userImage.type === userImageType.UploadLogo ||
      userImage.type === userImageType.SavedArt ||
      userImage.type === userImageType.SharedArt;

    if (allowToRecentCollection) {
      const imprintAreaDict = state.imprintAreaDict;
      const actionFire = (imprintAreaCode) => {
        dispatch(
          recentArtsInit({
            imprintAreaCode,
            recent: Object.assign({}, userImage, {
              guid: uuidv4(),
              x: 0,
              y: 0,
              zIndex: 0,
              colors: undefined,
            }),
          })
        );
      };
      const imprintAreaCodes = (
        userImage.type === userImageType.SavedArt || userImage.type === userImageType.SharedArt
          ? [imprintAreaDict[formatkey] && imprintAreaDict[formatkey].imprintAreaCode]
          : [
              ...Object.values(imprintAreaDict)
                .map((x) => x.imprintAreaCode)
                .filter((x, index, _this) => _this.indexOf(x) === index),
            ]
      ).filter(Boolean);
      imprintAreaCodes.forEach((x) => x && actionFire(x));
    }
  };

const applySelectedAsync =
  ({ vendorId, formatkey, userImage }) =>
  (dispatch, getState) => {
    //add recentArtDict
    //only StockArt and Upload for recentArt push
    const state = getState();
    const studioType = getStudioItemType(state);
    const isNextgenUi = getIsNextGenUI(state);
    const draggingArea = getCanvasDraggingArea(state);

    dispatch(recentArtsInitAsync({ userImage, formatkey }));
    //SavedArt
    if (userImage.type === userImageType.SavedArt) {
      dispatch(addSavedArtToImgDictProcess(vendorId, formatkey, userImage));
    } else {
      //StockArt and SharedArt
      //add imageListDict
      const newGuid = uuidv4(); //create new guid for imageLisDict for forbidden duplicate react key
      // const isColorized = checkShowImageColorized({
      //   studioType,
      //   userImage,
      //   imprintArea: state.imprintAreaDict[formatkey],
      // });

      dispatch(
        addNewElement(
          (elementId, layerIndex) =>
            dispatch(
              addArt({
                canvasKey: formatkey,
                artData: userImage,
                newGuid,
                elementId,
                layerIndex,
                centerArt: isNextgenUi,
                draggingArea,
                // isColorized,
                // isHotStamped: getCurrentHotStampedCheck(state),
              })
            ),
          userImage
        )
      );

      dispatch(
        userImageDimensionWatch({
          formatkey,
          userImage,
          watchGuid: newGuid,
          needToUpdateElementStatus: elementStatus.SingleSelected,
        })
      );

      if (studioType === studioItemType.digital && userImage.type === userImageType.UploadLogo)
        checkUploadLogoDpi(dispatch, userImage);
      dispatch(
        pushToUndoList(
          userImage.type === userImageType.StockArt ? undoRedoType.SelectStockArt : undoRedoType.SelectSharedArt
        )
      );
    }
    if (isNextgenUi) {
      dispatch(updateFullPreview());
      dispatch(hideWaitingCover());
    }
  };

/**
 * list choose section Apply Button logic
 * userImageEditNextProcess, use for NonDigital Studio
 * @param {string} vendorId
 * @param {string} formatkey
 * @param {object} userImage
 */
const userImageEditNextProcessAsync =
  ({ vendorId, formatkey, userImage, changeAction = changeFromPreviewAction.None }) =>
  (dispatch, getState) => {
    const state = getState();
    const studioType = getStudioItemType(state);
    const tempArt = getTempArt(state);

    if (studioType !== studioItemType.digital && userImage.type === userImageType.UploadLogo) {
      const logoEditingStatus = getLogoEditingStatus(state);
      initLogoFromRecent({
        dispatch,
        vendorId,
        formatkey,
        tempLogo: tempArt,
        callback: () => {
          gotoFirstStep(dispatch, logoEditingStatus);
        },
      });

      return;
    }

    dispatch(showWaitingCover());
    //create new guid for imageLisDict for forbidden duplicate react key
    //then can use setImageUI to update TempSelected UserImage
    const tempNewGuid = uuidv4();
    dispatch(
      updateElementByStatus({
        canvasKey: formatkey,
        status: elementStatus.TempSelected,
        ui: {
          guid: tempNewGuid,
        },
        //After refactoring the save art,
        //there is no need to clear the colors.
        //It is currently uncertain whether the other arts need to be cleared,
        //so they remain unchanged
        data: userImage.type === userImageType.SavedArt ? {} : { colors: null },
      })
    );
    dispatch(recentArtsInitAsync({ userImage, formatkey }));
    //SavedArt
    if (userImage.type === userImageType.SavedArt) {
      dispatch(savedArtEditNextProcess({ vendorId, formatkey, userImage, watchGuid: tempNewGuid, changeAction }));
    } else {
      //StockArt and SharedArt
      dispatch(
        userImageDimensionWatch({
          formatkey,
          userImage,
          watchGuid: tempNewGuid,
          needToUpdateElementStatus: elementStatus.SingleSelected,
        })
      );

      dispatch(
        setImageUI({
          type: 'show_edit_color_link',
          canvasKey: formatkey,
          guid: tempNewGuid,
          ui: { design: true }, //to show Edit Color Link Section in EditUserImage Panel
        })
      );

      /**
       * Non Digital Colorize Process
       */
      dispatch(
        userImageColorizeForNonDigitalAsync({
          formatkey,
          userImage,
          watchGuid: tempNewGuid,
          callback: () => {
            changeAction === changeFromPreviewAction.UserImage &&
              dispatch(
                keepUserImageColorAsync({
                  formatkey,
                  watchGuid: tempNewGuid,
                })
              );

            /**
             * ScreenPrintedDigital - colorize temp userimage then process it to SingleSelected status
             * NonDigital - if there is only one ImprintColor,
             *    or if multiple colors saved art,
             *    or if multiple color space shared art, process it to SingleSelected status
             */
            dispatch(autoApplySelectedProcess({ vendorId, formatkey, watchGuid: tempNewGuid }));
            dispatch(hideWaitingCover());
          },
        })
      );
    }
  };

/**
 * ScreenPrintedDigital - colorize temp userimage then process it to SingleSelected status
 * NonDigital - if there is only one ImprintColor,
 *    or if multiple colors saved/shared/stock art,
 *    process it to SingleSelected status
 * @param { vendorId, formatkey, watchGuid } param0
 */
const autoApplySelectedProcess =
  ({ vendorId, formatkey, watchGuid }) =>
  (dispatch, getState) => {
    const state = getState();
    const studioType = getStudioItemType(state);
    const imageWatch =
      state.imageDict[formatkey] && state.imageDict[formatkey].find((x) => x.ui && x.ui.guid === watchGuid);
    const autoCondition =
      (studioType === studioItemType.screenPrintedDigital &&
        imageWatch.data.type !== userImageType.Slogan &&
        imageWatch.data.type !== userImageType.Verse &&
        imageWatch.data.type !== userImageType.Sentiment) ||
      (studioType === studioItemType.screenPrintedDigital &&
        (imageWatch.data.type === userImageType.Slogan ||
          imageWatch.data.type === userImageType.Verse ||
          imageWatch.data.type === userImageType.Sentiment) &&
        (getHasOneImprintColorOnly(state, formatkey) ||
          (imageWatch &&
            imageWatch.data &&
            (imageWatch.data.type === userImageType.StockArt ||
              imageWatch.data.type === userImageType.SharedArt ||
              imageWatch.data.type === userImageType.SavedArt) &&
            imageWatch.data.colors &&
            imageWatch.data.colors.length > 1)));

    if (autoCondition) {
      imageWatch &&
        imageWatch.data &&
        dispatch(
          applySelectedAsync({
            vendorId,
            formatkey,
            userImage: imageWatch.data,
          })
        );
    }
  };

/**
 * Cancel Button logic when Change UserImage
 * use for NonDigital Studio
 * @param {string} formatkey
 * @param {object} image
 */
const cancelUserImageChangeProcessAsync =
  ({ formatkey, image }) =>
  (dispatch, getState) => {
    dispatch(showWaitingCover());
    //create new guid for imageLisDict for forbidden duplicate react key
    //then can use setImageUI to update TempSelected UserImage
    const tempNewGuid = uuidv4();
    dispatch(
      updateElementByStatus({
        canvasKey: formatkey,
        status: elementStatus.TempSelected,
        ui: {
          guid: tempNewGuid,
        },
        data: { colors: null },
      })
    );

    dispatch(
      setImageUIAsync({
        type: 'newGuid',
        canvasKey: formatkey,
        guid: tempNewGuid,
        data: Object.assign({}, image.data),
        ui: Object.assign({}, image.ui, {
          guid: tempNewGuid,
        }),
      })
    );

    //for restoreElementColorAsync
    dispatch(
      setUserImageCloneAsync(
        Object.assign({}, image, {
          ui: Object.assign({}, image.ui, {
            guid: tempNewGuid,
          }),
        })
      )
    );

    dispatch(hideWaitingCover());
  };

/**
 * Only use for NonDigital Studio SavedArt
 * list choose section Apply Button logic
 * addSavedArtToImgDictProcess(savedArtEditNextProcess) NonDigital Studio
 * @param {string} vendorId
 * @param {string} formatkey
 * @param {object} userImage
 */
const savedArtEditNextProcess =
  ({ vendorId, formatkey, userImage, watchGuid, changeAction }) =>
  (dispatch, getState) => {
    //to show Edit Color Link Section in EditUserImage Panel
    dispatch(
      setImageUI({
        type: 'show_saved_edit_color_link',
        canvasKey: formatkey,
        guid: watchGuid,
        ui: { design: true },
      })
    );
    dispatch(autoApplySelectedProcess({ vendorId, formatkey, watchGuid }));
  };

/**
 * Only use for (ScreenPrinted)Digital Studio SavedArt
 * change to SingleSelected imageDict store
 * addSavedArtToImgDictProcess (ScreenPrinted)Digital Studio
 * @param {string} vendorId
 * @param {string} canvasKey
 * @param {object} userImage
 */
const addSavedArtToImgDictProcess = (vendorId, canvasKey, userImage) => (dispatch, getState) => {
  const state = getState();
  const studioType = getStudioItemType(state);
  const isNextgenUi = getIsNextGenUI(state);
  const draggingArea = getCanvasDraggingArea(state);

  dispatch(
    addNewElement((elementId, layerIndex) => {
      const watchGuid = uuidv4();
      dispatch(
        addArt({
          canvasKey,
          artData: userImage,
          newGuid: watchGuid, //uuidv4(), create new guid for imageLisDict for forbidden duplicate react key
          elementId,
          layerIndex,
          isColorized: studioType === studioItemType.digital || (userImage.colors && userImage.colors.length <= 1),
          isHotStamped: getCurrentHotStampedCheck(state),
          centerArt: isNextgenUi,
          draggingArea,
        })
      );
      dispatch(pushToUndoList(undoRedoType.SelectSavedArt));
    })
  );
  dispatch(hideWaitingCover());
};

/**
 * Only use for (ScreenPrinted)Digital Studio SavedArt
 * change to SingleSelected imageDict store
 * addSavedArtToImgDictProcess (ScreenPrinted)Digital Studio
 * @param {string} vendorId
 * @param {string} formatkey
 * @param {object} userImage
 */
export const savedArtDimensionWatch =
  ({ formatkey, userImage, resolve, reject }) =>
  (dispatch, getState) => {
    let state = getState();
    const vendorId = getVendorId(state);
    const format = getCurrentImprintFormat(state, formatkey);
    if (format !== null) {
      // const studioType = getStudioItemType(state);
      const hasOriginalSize =
        userImage.originalWidth !== 'undefined' &&
        userImage.originalWidth > 0 &&
        userImage.originalHeight !== 'undefined' &&
        userImage.originalHeight > 0;
      if (!hasOriginalSize) {
        dispatch(showWaitingCover());
        getSavedArtDimension(vendorId, userImage.fileName, format.processType, format.imprintAreaCode)
          .then((res) => {
            if (res && res.data) {
              dispatch(setUserImageOriginalSizeAsync(userImage, res.data));
              resolve && resolve({ formatkey, userImage, dimension: res.data });
            } else {
              reject && reject({ formatkey });
            }
            dispatch(hideWaitingCover());
          })
          .catch((_) => {
            dispatch(hideWaitingCover());
            reject && reject({ formatkey });
          });
      } else {
        resolve && resolve({ formatkey, userImage, dimension: null });
      }
    }
  };
export const setUserImageOriginalSizeAsync = (userImage, dimension) => (dispatch) => {
  dispatch(setUserImageDimension({ userImage, dimension }));
};
const tempSelectedAsync =
  ({ formatkey, userImage, changeAction = changeFromPreviewAction.None }) =>
  (dispatch, getState) => {
    const state = getState();
    const draggingArea = getCanvasDraggingArea(state);
    const userImageClone = getUserImageClone(state);
    const studioType = getStudioItemType(state);
    const isNextgenUi = getIsNextGenUI(state);
    const canvasData = getCurrentCanvasData(state);

    // firstly, remove temp atr if exist
    //const tempArt = imageList.find(element => element.ui.status == elementStatus.TempSelected);
    const newArt = {
      data: {},
      ui: {
        guid: userImage.guid,
        status: elementStatus.TempSelected,
        design: false,
      },
    };
    let x = draggingArea.x;
    let y = draggingArea.y;

    //change StockArt
    if (userImageClone) {
      //console.log(userImageClone);
      x = userImageClone.x;
      y = userImageClone.y;
    }
    Object.assign(newArt.data, userImage);
    delete newArt.data.guid;
    newArt.data.x = x;
    newArt.data.y = y;
    newArt.data.direction = 0;
    newArt.data.zIndex = getImageLayerIndex(canvasData, true);

    if (userImage.type === userImageType.SavedArt) {
      const newSize = getAdjustSize(userImage.thumbnailWidth, userImage.thumbnailHeight, draggingArea);
      newArt.ui.url = userImage.thumbnail;
      newArt.data.width = newSize.width;
      newArt.data.height = newSize.height;
      const newPos = getAdjustPosition(
        {
          x: newArt.data.x,
          y: newArt.data.y,
        },
        { ...newSize },
        draggingArea
      );
      newArt.data.x = newPos.x;
      newArt.data.y = newPos.y;
    }
    //stockart,sharedart, uploadart( from recentart)
    else {
      const hasOriginalSize =
        userImage.originalWidth !== 'undefined' &&
        userImage.originalWidth > 0 &&
        userImage.originalHeight !== 'undefined' &&
        userImage.originalHeight > 0;

      if (hasOriginalSize) {
        newArt.data.originalWidth = userImage.originalWidth;
        newArt.data.originalHeight = userImage.originalHeight;
        let newSize = {};
        if (userImage.type === userImageType.UploadLogo && !userImage.finalCropRect.isEmpty) {
          newSize = getAdjustSize(userImage.finalCropRect.width, userImage.finalCropRect.height, draggingArea);
        } else {
          newSize = getAdjustSize(userImage.originalWidth, userImage.originalHeight, draggingArea);
          if (userImage.type === userImageType.StockArt) {
            //because the default stock art size is too large
            //if the ratio of the long border to the short border of the canvas is less than 3
            //the stock art is scaled down to 0.3 times
            const minDraggingArea = Math.min(draggingArea.width, draggingArea.height);
            const maxDraggingArea = Math.max(draggingArea.width, draggingArea.height);
            const ratio = maxDraggingArea / minDraggingArea;

            if (ratio < 3) {
              newSize.width = Math.round(newSize.width * REDUCE_CANVAS_CONTAINER_SIZE);
              newSize.height = Math.round(newSize.height * REDUCE_CANVAS_CONTAINER_SIZE);
            }
          }
        }
        newArt.data.width = newSize.width;
        newArt.data.height = newSize.height;
        const newPos = getAdjustPosition(
          {
            x: newArt.data.x,
            y: newArt.data.y,
          },
          { ...newSize },
          draggingArea
        );
        newArt.data.x = newPos.x;
        newArt.data.y = newPos.y;
      }
    }

    if (userImage.type === userImageType.SavedArt) {
      dispatch(
        savedArtDimensionWatch({
          formatkey,
          userImage: newArt.data,
          resolve: ({ dimension }) => {
            newArt.data = { ...newArt.data, ...dimension };
            dispatch(
              addTemp({
                canvasKey: formatkey,
                draggingArea,
                studioItemType: studioType,
                newArt,
                centerArt: isNextgenUi,
              })
            );
            dispatch(
              userImageColorizeForNonDigitalAsync({
                formatkey,
                userImage: newArt.data,
                watchGuid: userImage.guid,
                callback: () => {
                  dispatch(
                    keepUserImageColorAsync({
                      formatkey,
                      watchGuid: userImage.guid,
                    })
                  );
                },
              })
            );
          },
          reject: () => {
            dispatch(hideWaitingCover());
            dispatch(
              showMessageBox({
                show: true,
                message: ART_CAN_NOT_USED_MESSAGE,
              })
            );
          },
        })
      );
    } else {
      //loading
      dispatch(
        addTemp({
          canvasKey: formatkey,
          draggingArea,
          studioItemType: studioType,
          newArt,
          centerArt: isNextgenUi,
        })
      );
      // if select a uploaded logo from RecentArt
      if (studioType !== studioItemType.digital && userImage.type === userImageType.UploadLogo) {
        dispatch(changeLogoEditingStatus(logoEditingStatusEnum.NonDigitalLogoAsRecent));
        return;
      }
      dispatch(
        userImageDimensionWatch({
          formatkey,
          userImage,
          watchGuid: userImage.guid,
          needToUpdateElementStatus: elementStatus.TempSelected,
        })
      );
    }

    /**
     * Non Digital and ScreenPrinted Digital Item Colorize Process
     * There is a Preview button in Digital and ScreenPrinted Digital Studio, the user can press that to update preview
     */
    if (studioType !== studioItemType.digital) {
      if (userImage.type !== userImageType.SavedArt) {
        dispatch(
          userImageColorizeForNonDigitalAsync({
            formatkey,
            userImage,
            watchGuid: userImage.guid,
            callback: () => {
              dispatch(
                keepUserImageColorAsync({
                  formatkey,
                  watchGuid: userImage.guid,
                })
              );
              //There is a Preview button in Digital and ScreenPrinted Digital Studio, the user can press that to update preview
              if (studioType !== studioItemType.screenPrintedDigital) {
                dispatch(showWaitingCover());
                dispatch(updateFullPreview());
              }
            },
          })
        );
      }
    } else {
      // digital studio
      dispatch(
        keepUserImageColorAsync({
          formatkey,
          watchGuid: userImage.guid,
        })
      );
    }
  };

const combineImageDetectedColors = (detectedColors, currentColors) => {
  const result = [];

  [...currentColors, ...detectedColors].some((color) => {
    if (!result.some((clr) => clr.colorId === color.colorId)) {
      result.push(color);
    }
    return result.length === detectedColors.length;
  });

  return result;
};

/**
 * Colorize UserImage in NonDigital Studio
 * @param {string} formatKey
 * @param {object} userImage
 * @param {string} watchGuid
 * @param {boolean} needToUpdateElementStatus
 */
export const userImageColorizeForNonDigitalAsync =
  ({ formatkey, userImage, watchGuid, callback }) =>
  (dispatch, getState) => {
    const state = getState();
    const studioType = getStudioItemType(state);
    //const isHotStamped = getCurrentHotStampedCheck(state);
    if (studioType !== studioItemType.digital) {
      //make use of Text Color to assign UserImage Color
      const userImageDefaultAssemble = () => {
        const [defaultColor, isBackup] = getDefaultUserImageColor(state, formatkey);
        if (defaultColor) {
          const colors = [defaultColor];
          dispatch(
            setImageUI({
              type: 'colorize_img',
              canvasKey: formatkey,
              guid: watchGuid,
              ui: {
                canEdit: !(
                  userImage.type === userImageType.SharedArt && userImage.colorSpace === colorSpace.MultipleColor
                ),
                topColorCount: 1,
              },
              data: { colors },
            })
          );

          if (outsideUserImageMatch(userImage) && !isBackup) {
            dispatch(recordTextColorAsync({ formatkey, color: defaultColor }));
          }
        }
      };
      switch (userImage.type) {
        case userImageType.StockArt: {
          if (userImage.colorType === stockArtColorType.Process) {
            dispatch(
              colorizeCheckColor({
                studioType,
                formatkey,
                imageData: userImage,
                onComplete: (colors) => {
                  if (colors && colors.length > 0) {
                    //can change savedart logo
                    dispatch(changeLogoColorAsync({ formatkey, guid: watchGuid, colors }));
                    dispatch(
                      setImageUI({
                        type: 'colorize_stock',
                        canvasKey: formatkey,
                        guid: watchGuid,
                        ui: {
                          canEdit: colors.length === 1,
                          topColorCount: colors.length,
                        },
                      })
                    );
                  } else {
                    /**
                     *  else {
                     *    nothing to do,
                     *    has get default ImprintColor after StockArt Thumbnail click
                     *  }
                     */
                    userImageDefaultAssemble();
                  }
                  callback && callback();
                },
              })
            );
          } else {
            userImageDefaultAssemble();
            callback && callback();
          }
          break;
        }
        case userImageType.SavedArt: {
          dispatch(
            colorizeCheckColor({
              studioType,
              formatkey,
              imageData: userImage,
              onComplete: (colors) => {
                if (colors && colors.length > 0) {
                  //assignment after colorized, then colorSpace make effect: shareArt.colorSpace = colors.length > 1 ? colorSpace.multipleSpace : colorSpace.singleSpace
                  //show original url for multiple color space shared
                  //change savedart logo
                  const allColorsInUse = getAllColorsInUse(state, formatkey);
                  const newColors = combineImageDetectedColors(colors, allColorsInUse);
                  dispatch(changeLogoColorAsync({ formatkey, guid: watchGuid, colors: newColors }));
                  dispatch(
                    setImageUI({
                      type: 'colorize_saved',
                      canvasKey: formatkey,
                      guid: watchGuid,
                      ui: {
                        canEdit: colors.length === 1, //can't edit color for multiple color space shared
                        topColorCount: colors.length,
                      },
                      data: {
                        colorSpace: colors.length > 1 ? colorSpace.MultipleColor : colorSpace.SingleColor,
                      },
                    })
                  );
                } else {
                  /**
                   *  else {
                   *    nothing to do,
                   *    has get default ImprintColor after SavedArt Thumbnail click
                   *  }
                   */
                  userImageDefaultAssemble();
                }
                callback && callback();
              },
            })
          );

          break;
        }
        case userImageType.SharedArt: {
          const currentImprintAreaKey = getCurrentImprintAreaKey(state);
          const selectedTextColor = getSelectedTextColor(state, currentImprintAreaKey);
          const maxColorCount = getImprintOptionMaxColorCount(state);
          //colorSpace from DAM Service, we can use it directly
          if (userImage.colorSpace === colorSpace.MultipleColor && !(selectedTextColor && maxColorCount === 1)) {
            dispatch(
              colorizeCheckColor({
                studioType,
                formatkey,
                imageData: userImage,
                onComplete: (colors) => {
                  if (colors && colors.length > 0) {
                    //just record colorize colors
                    //show Show original(DO NOT COLORIZE) sharedart
                    const allColorsInUse = getAllColorsInUse(state, formatkey);
                    const newColors = combineImageDetectedColors(colors, allColorsInUse);
                    dispatch(changeLogoColorAsync({ formatkey, guid: watchGuid, colors: newColors }));
                    dispatch(
                      setImageUI({
                        type: 'colorize_shared',
                        canvasKey: formatkey,
                        guid: watchGuid,
                        ui: {
                          canEdit: false,
                          topColorCount: colors.length,
                        },
                      })
                    );
                  } else {
                    /**
                     *  else {
                     *    nothing to do,
                     *    has get default ImprintColor after SharedArt Thumbnail click
                     *  }
                     */
                    userImageDefaultAssemble();
                  }
                  callback && callback();
                },
              })
            );
          } else {
            /**
             * SingleColor ColorSpace SharedArt
             * nothing to do,
             * has get default ImprintColor after SharedArt Thumbnail click
             */
            userImageDefaultAssemble();
            callback && callback();
          }
          break;
        }
        case userImageType.Verse:
        case userImageType.Sentiment: {
          userImageDefaultAssemble();
          callback && callback();
          break;
        }
        case userImageType.Slogan: {
          userImageDefaultAssemble();
          callback && callback();
          break;
        }
        default: {
          userImageDefaultAssemble();
          callback && callback();
          // nothing to do
          break;
        }
      }
    }
  };

export const colorizeCheckColor =
  ({ studioType, formatkey, imageData, onComplete }) =>
  (dispatch, getState) => {
    const state = getState();
    const currentImprintProcessType = getCurrentImprintProcessType(state, formatkey);
    const colorList = getImprintColors(state, formatkey);
    if (currentImprintProcessType === ProcessTypes.FullColorScreened) {
      const fullImprintColor = colorList.find((color) => !color.colorReference);
      const colors = [fullImprintColor].filter(Boolean);
      onComplete && onComplete(colors);
    } else {
      const vendorId = getVendorId(state);
      const isLiteParentChildItem = getIsLiteParentChildItem(state);
      const itemId = isLiteParentChildItem ? getChildItemIdFromBomSelectionForLite(state) : getItemId(state);
      const maxColorCount = getImprintOptionMaxColorCount(state, formatkey);
      const checkColorRequest = {
        vendorId,
        itemId,
        maxColorCount,
        colorList,
        imageView: Object.assign({}, imageData, { studioItemType: studioType }),
        frontId: uuidv4(),
      };
      getColorsFromColorizeService(checkColorRequest)
        .then((res) => {
          fillColorCmyk(res.data, colorList);
          onComplete && onComplete(res.data);
          dispatch(hideWaitingCover());
        })
        .catch((err) => {
          console.log(err);
          dispatch(hideWaitingCover());
          dispatch(showMessageBox(err.message));
        });

      dispatch(showWaitingCover({ showProgress: false }));
    }
  };

export const getAdjustPosition = (pos, imageSize, draggingArea, direction = 0) => {
  const adjustedElement = keepElementInCanvas(draggingArea, pos, imageSize, direction);
  return { x: adjustedElement.x, y: adjustedElement.y };
};

export const getAdjustSize = (originalWidth, originalHeight, draggingArea) => {
  if (originalWidth > draggingArea.width || originalHeight > draggingArea.height) {
    return adjustImageSizeToFitContainer(
      {
        width: originalWidth,
        height: originalHeight,
      },
      {
        width: draggingArea.width,
        height: draggingArea.height,
      }
    );
  } else {
    return {
      width: originalWidth,
      height: originalHeight,
    };
  }
};

/**
 * do ImageDimensionWatch asynchronous userimage temp selected
 * update imgDict in store
 * excepting SavedArt
 */
export const userImageDimensionWatch =
  ({ formatkey, userImage, watchGuid, needToUpdateElementStatus }) =>
  (dispatch, getState) => {
    //don't Watch on SavedArt
    if (userImage.type !== userImageType.SavedArt) {
      const hasOriginalSize =
        userImage.originalWidth !== 'undefined' &&
        userImage.originalWidth > 0 &&
        userImage.originalHeight !== 'undefined' &&
        userImage.originalHeight > 0;

      if (!hasOriginalSize) {
        let state = getState();
        const draggingArea = getCanvasDraggingArea(state, formatkey);
        const studioItemType = getStudioItemType(state);
        getImageDimension(studioItemType, userImage.scene7FileName, (dimension) => {
          dispatch(setUserImageOriginalSizeAsync(userImage, dimension));
          let newSize = {};
          if (userImage.type === userImageType.UploadLogo && !userImage.finalCropRect.isEmpty) {
            newSize = getAdjustSize(userImage.finalCropRect.width, userImage.finalCropRect.height, draggingArea);
          } else {
            newSize = getAdjustSize(dimension.width, dimension.height, draggingArea);
          }
          const newPos = getAdjustPosition(
            {
              x: userImage.x,
              y: userImage.y,
            },
            {
              ...newSize,
            },
            draggingArea
          );
          state = getState();

          const imageDict = state.imageDict;
          let userImageData = Object.assign({}, userImage);
          const foundArt =
            imageDict[formatkey] &&
            imageDict[formatkey].find((x) => x.ui.guid === watchGuid && x.ui.status === needToUpdateElementStatus);
          if (foundArt) {
            userImageData = foundArt.data;
          }
          const data = Object.assign({}, userImageData, {
            originalWidth: dimension.width,
            originalHeight: dimension.height,
            width: newSize.width,
            height: newSize.height,
            ...newPos,
          });
          // const isColorized = checkShowImageColorized({
          //   studioType: getStudioItemType(state),
          //   userImage,
          //   imprintArea: state.imprintAreaDict[formatkey],
          // });
          // const isHotStamped = getCurrentHotStampedCheck(state);
          const updateProps = {
            ...data,
            guid: watchGuid,
          };
          dispatch(
            updateElementDimension({
              canvasKey: formatkey,
              updateProps,
              elementStatus: needToUpdateElementStatus,
            })
          );
          dispatch(
            setImageUI({
              type: 'saved_dimension',
              canvasKey: formatkey,
              guid: watchGuid,
              data: updateProps,
            })
          );
        });
      }
    }
  };

const cancelSelectedAsync = (canvasKey) => (dispatch) => {
  dispatch(cancelSelectArt({ canvasKey }));
};
const changeDigitalArtColorAsync =
  ({ canvasKey, guid, colors }) =>
  (dispatch, getState) => {
    dispatch(
      changeArtColorAction({
        canvasKey,
        guid,
        colors,
        isHotStamped: getCurrentHotStampedCheck(getState()),
        isColorized: true,
      })
    );
  };
const setSavedArtThumbnailAsync = (savedPayload) => (dispatch, getState) => {
  const { pageIndex, index, savedart, savedPreview } = savedPayload;
  const state = getState();
  const currentImprintFormat = getCurrentImprintFormat(state);
  dispatch(
    setSavedArtThumbnail({
      processType: currentImprintFormat.processType,
      imprintAreaCode: currentImprintFormat.imprintAreaCode,
      pageIndex,
      index,
      fileName: savedart.fileName, //for recent list
      userImageType: userImageType.SavedArt, //for recent list
      thumbnail: savedPreview.url,
      thumbnailHeight: savedPreview.height,
      thumbnailWidth: savedPreview.width,
    })
  );

  //to find saved art in imageDict to make sure its width/height is not null
  if (state.imageDict[currentImprintFormat.key]) {
    const lazySavedArt = state.imageDict[currentImprintFormat.key].find(
      (x) =>
        x.data.type === userImageType.SavedArt &&
        x.data.fileName === savedart.fileName &&
        (!x.data.url || (!x.data.width && !x.data.height))
    );
    if (lazySavedArt) {
      const studioType = getStudioItemType(state);
      const imageData = { ...savedPreview, thumbnail: savedPreview.url };
      const draggingArea = getCanvasDraggingArea(state, currentImprintFormat.key);
      adjustLogoOriginSizeByLimitation(imageData, studioType, draggingArea);

      if (lazySavedArt.data && lazySavedArt.data.width) {
        delete imageData.width;
      }
      if (lazySavedArt.data && lazySavedArt.data.height) {
        delete imageData.height;
      }
      if (lazySavedArt.data && lazySavedArt.data.url) {
        delete imageData.url;
      }
      if (lazySavedArt.data && lazySavedArt.data.thumbnail) {
        delete imageData.thumbnail;
      }

      dispatch(
        setImageUI({
          type: 'set_lazySavedArt_w_d',
          canvasKey: currentImprintFormat.key,
          guid: lazySavedArt.ui.guid,
          data: { ...imageData },
        })
      );
    }
  }
};

const setUserImageCloneAsync =
  (userImage = null) =>
  (dispatch) => {
    dispatch(setUserImageClone(userImage));
  };
const setImageUIAsync = (params) => (dispatch) => {
  dispatch(setImageUI(params));
};
const colorCompare = (clr0, clr1) => {
  return (
    clr0.colorId === clr1.colorId && clr0.colorReference === clr1.colorReference && clr0.colorType === clr1.colorType
  );
};

const userImageColorsDiffCheck = (clr0, clr1) => {
  return (
    !clr0 ||
    !clr1 ||
    clr0.length !== clr1.length ||
    (() => {
      let _equal = false;
      clr0.forEach((_clr, i) => {
        _equal = _equal || colorCompare(_clr, clr1[i]);
      });
      return !_equal;
    })()
  );
};
const restoreElementColorAsync =
  ({ formatkey, imgClrsClone, imgClrsChanged, txtCrlClone, txtClrChanged }) =>
  (dispatch, getState) => {
    const state = getState();
    const image = getImageElementClone(state);
    const guid = image && image.ui ? image.ui.guid : '';
    let txtClrRestored = false;
    let imgClrRestored = false;
    if (txtCrlClone && !colorCompare(txtCrlClone, txtClrChanged)) {
      dispatch(changeTextColorAsync({ formatkey, color: txtCrlClone }));
      txtClrRestored = true;
    }
    if (imgClrsClone) {
      if (userImageColorsDiffCheck(imgClrsClone, imgClrsChanged)) {
        dispatch(
          setImageUIAsync({
            type: 'singleSelected_colors',
            canvasKey: formatkey,
            guid,
            data: {
              colors: imgClrsClone,
            },
            ui: {
              status: elementStatus.SingleSelected,
            },
          })
        );
        imgClrRestored = true;
      }
    }

    //if text and image color have not been changed, just restore it's status and url
    if (!txtClrRestored || !imgClrRestored) {
      dispatch(
        setImageUIAsync({
          type: 'restore_status_url',
          canvasKey: formatkey,
          guid,
          ui: {
            status: elementStatus.SingleSelected,
            url: image.ui.url,
          },
        })
      );
    }
  };

const userImageAssembleAsync = () => (dispatch, getState) => {
  let state = getState();
  const imageDict = state.imageDict;
  Object.keys(imageDict).forEach((formatkey) => {
    const imageList = imageDict[formatkey];
    if (imageList && imageList.length > 0) {
      imageList.forEach((image) => {
        if (image.data) {
          //SavedArt
          if (image.data.type === userImageType.SavedArt) {
            const resolve = (props) => {
              const { formatkey, userImage, dimension } = props;
              let artData = Object.assign({}, userImage);
              state = getState();
              //const studioType = getStudioItemType(state);
              if (dimension) {
                dispatch(setUserImageOriginalSizeAsync(userImage, dimension));
                artData = Object.assign({}, artData, {
                  ...{
                    originalHeight: dimension.originalHeight,
                    originalWidth: dimension.originalWidth,
                  },
                });
              }

              dispatch(
                setImageUI({
                  type: 'saved_assemble',
                  canvasKey: formatkey,
                  guid: image.ui.guid,
                  data: { ...artData },
                })
              );
            };
            dispatch(savedArtDimensionWatch({ formatkey, userImage: image.data, resolve }));
          } else {
            //StockArt, SharedArt, Slogan and so on
            dispatch(
              userImageDimensionWatch({
                formatkey,
                userImage: image.data,
                watchGuid: image.ui.guid,
                needToUpdateElementStatus: image.ui.elementStatus,
              })
            );
          }
        }
      });
    }
  });
};

/**
 * keepUserImageColorAsync
 * @param {*} {  formatkey, changeAction: changeFromPreviewAction.UserImage|Color|None }
 */
export const keepUserImageColorAsync =
  ({ formatkey, watchGuid, comparedUserImage }) =>
  (dispatch, getState) => {
    const state = getState();
    const imageClone = comparedUserImage || getUserImageClone(state);
    if (imageClone) {
      const imageWatch =
        state.imageDict[formatkey] && state.imageDict[formatkey].find((x) => x.ui && x.ui.guid === watchGuid);
      const userImage = imageWatch && imageWatch.data;
      if (userImage && normalUserImageMatch(imageClone) && normalUserImageMatch(userImage)) {
        if (userImageColorsDiffCheck(imageClone.colors, userImage.colors)) {
          dispatch(changeLogoColorAsync({ formatkey, guid: watchGuid, colors: imageClone.colors }));
        }
      }
    }
  };

export const userImageMandatoryMissingCheckAsync =
  ({ formatkey, resolve, reject }) =>
  (dispatch, getState) => {
    const mandatory = { missing: false, type: userImageType.NotSet, warning: '' };
    const state = getState();
    const studioType = getStudioItemType(state);
    if (studioType === studioItemType.nonDigital) {
      const imageList = getCanvasImageList(state, formatkey);
      const format = getCurrentImprintFormat(state, formatkey);
      if (
        format.sloganAvailable &&
        getSloganMandatoryCheck(state, format.imprintAreaCode) &&
        !imageList.find((x) => x.data && x.data.type === userImageType.Slogan)
      ) {
        mandatory.missing = true;
        mandatory.type = userImageType.Slogan;
        mandatory.warning = mandatoryMissingWarning[userImageType.Slogan];
      } else if (
        getVerseAvailable(state, formatkey) &&
        format.mandatoryVerse &&
        !imageList.find((x) => x.data && x.data.type === userImageType.Verse)
      ) {
        mandatory.missing = true;
        mandatory.type = userImageType.Verse;
        mandatory.warning = mandatoryMissingWarning[userImageType.Verse];
      } else if (
        getSentimentAvailable(state, formatkey) &&
        format.mandatorySentiment &&
        !imageList.find((x) => x.data && x.data.type === userImageType.Sentiment)
      ) {
        mandatory.missing = true;
        mandatory.type = userImageType.Sentiment;
        mandatory.warning = mandatoryMissingWarning[userImageType.Sentiment];
      }
    }
    if (mandatory.missing) {
      reject && reject(mandatory);
    } else {
      resolve && resolve(mandatory);
    }
  };

export const specialImageMandatoryMissingCheckAsync =
  ({ formatkey }) =>
  (dispatch, getState) => {
    let type = userImageType.NotSet;
    const state = getState();
    const imageList = getCanvasImageList(state, formatkey);
    const format = getCurrentImprintFormat(state, formatkey);
    if (
      format.sloganAvailable &&
      getSloganMandatoryCheck(state, format.imprintAreaCode) &&
      !imageList.find((x) => x.data && x.data.type === userImageType.Slogan)
    ) {
      type = userImageType.Slogan;
    } else if (
      getVerseAvailable(state, formatkey) &&
      format.mandatoryVerse &&
      !imageList.find((x) => x.data && x.data.type === userImageType.Verse)
    ) {
      type = userImageType.Verse;
    } else if (
      getSentimentAvailable(state, formatkey) &&
      format.mandatorySentiment &&
      !imageList.find((x) => x.data && x.data.type === userImageType.Sentiment)
    ) {
      type = userImageType.Sentiment;
    }
    return type;
  };

export {
  getSloganList,
  getVerseList,
  getSentimentList,
  getSavedArtPreview,
  getStockArtListByCategory,
  getSavedArtListByPage,
  getSharedArtList,
  getSavedArtCount,
  getStockArtCategories,
  getStockArtListBySearch,
  applySelectedAsync,
  cancelSelectedAsync,
  tempSelectedAsync,
  changeDigitalArtColorAsync,
  //userImagePrepare,
  setSavedArtThumbnailAsync,
  stockArtDataFormat,
  setUserImageCloneAsync,
  userImageEditNextProcessAsync,
  setImageUIAsync,
  cancelUserImageChangeProcessAsync,
  restoreElementColorAsync,
  userImageAssembleAsync,
};

export const getUserImageIconProps = (step) => {
  let icon = {
    src: '',
    desc: '',
  };
  const getIconSrc = (icon) => `/resources/assets/ui/desktop/${icon}`;
  switch (step) {
    case userImageStep.Upload:
      icon = {
        src: getIconSrc('upload.svg'),
        desc: 'Upload My Own',
      };
      break;
    case userImageStep.Stock:
      icon = {
        src: getIconSrc('stockart.svg'),
        desc: 'Stock Art & Mascots',
      };
      break;
    case userImageStep.Slogan:
      icon = {
        src: getIconSrc('slogan.svg'),
        desc: 'Slogan Gallery',
      };
      break;
    case userImageStep.Recent:
      icon = {
        src: getIconSrc('recentartwork.svg'),
        desc: 'Recent Artwork',
      };
      break;
    case userImageStep.Saved:
      icon = {
        src: getIconSrc('savedartwork.svg'),
        desc: 'Saved Artwork',
      };
      break;
    default:
      //icon = null;
      break;
  }
  return icon;
};

export const getUserImageIconEvent = (step, delegate) => {
  let onClickFunc = () => {};
  switch (step) {
    case userImageStep.Stock:
      onClickFunc = () => GoogleTagManager(() => delegate(step), 'studio_stockartandmascots')();
      break;
    case userImageStep.Slogan:
      onClickFunc = () => GoogleTagManager(() => delegate(step), 'studio_slogan')();
      break;
    case userImageStep.Recent:
      onClickFunc = () => delegate(step);
      break;
    case userImageStep.Saved:
      onClickFunc = () => GoogleTagManager(() => delegate(step), 'studio_savedandshared')();
      break;
    default:
      break;
  }
  return onClickFunc;
};

export const savedArtPreviewLoadHandler =
  (pageIndex = 0) =>
  (dispatch, getState) => {
    const state = getState();
    const saved = getSavedArts(state);
    const currentImprintFormat = getCurrentImprintFormat(state);
    saved &&
      saved.list &&
      saved.list[pageIndex] &&
      saved.list[pageIndex].forEach((savedart, index) => {
        if (!savedart.thumbnail) {
          getSavedArtPreview(
            savedart.fileName,
            currentImprintFormat.processType,
            currentImprintFormat.imprintAreaCode
          ).then((response) => {
            const savedPreview = response.data;
            dispatch(setSavedArtThumbnailAsync({ pageIndex: pageIndex, index, savedart, savedPreview }));
          });
        }
      });
  };
/**
 * to avoid to conflict action captured in Middleware
 * @param {formatKey, element} param0
 */
export const changeElementToTempAsync =
  ({ formatkey, element }) =>
  (dispatch) => {
    dispatch(
      changeElementStatusByGuidAsync({
        formatkey,
        element,
        changed: elementStatus.TempSelected,
      })
    );
  };

/**
 * to avoid to conflict action captured in Middleware
 * @param {formatKey, element} param0
 */
export const changeElementToSingleAsync =
  ({ formatkey, element }) =>
  (dispatch) => {
    dispatch(
      changeElementStatusByGuidAsync({
        formatkey,
        element,
        changed: elementStatus.SingleSelected,
      })
    );
  };
export const changeElementStatusByGuidAsync =
  ({ formatkey, element, changed }) =>
  (dispatch, getState) => {
    const elementUi = element && element.ui;
    if (elementUi) {
      dispatch(
        setImageUIAsync({
          type: 'element_status',
          canvasKey: formatkey,
          guid: elementUi.guid,
          ui: {
            status: changed,
          },
        })
      );
    }
  };
export const removeSavedArtFromRecentAsync = () => (dispatch) => {
  dispatch(removeSavedArtFromRecent());
};

export const scrollToStart = (dom) => {
  if (dom) {
    scrollIntoView(dom, {
      behavior: 'instant',
      scrollMode: 'if-needed',
      inline: 'start',
      boundary: dom.parent || undefined,
    });
  }
};

export const initMandatorySingleUserImage = (type) => (dispatch, getState) => {
  const state = getState();
  const currentImprintFormat = getCurrentImprintFormat(state);
  const autoSelectedSingleImage = (imprintAreaKey, image) => {
    dispatch(tempSelectedAsync({ formatkey: imprintAreaKey, userImage: image }));
    const vendorId = getVendorId(state);
    const tempUserImage = getTempUserImage(getState(), imprintAreaKey);
    dispatch(
      userImageEditNextProcessAsync({
        vendorId: vendorId,
        formatkey: imprintAreaKey,
        userImage: tempUserImage,
      })
    );
    dispatch(
      applySelectedAsync({
        vendorId: vendorId,
        formatkey: imprintAreaKey,
        userImage: tempUserImage,
      })
    );
  };
  const imageList = getCanvasImageList(state);
  if (
    type === userImageType.Slogan &&
    getSloganMandatoryCheck(state, currentImprintFormat.imprintAreaCode) &&
    onlyOneSloganCheck(state, true, currentImprintFormat.imprintAreaCode) &&
    !imageList.some((x) => x.data && x.data.type === userImageType.Slogan && x.data.isOutside)
  ) {
    const sloganList = getInnerOrOuterSlogan(state, true, currentImprintFormat.imprintAreaCode);
    sloganList && sloganList[0] && autoSelectedSingleImage(currentImprintFormat.key, sloganList[0]);
  } else if (
    type === userImageType.Verse &&
    currentImprintFormat.mandatoryVerse &&
    onlyOneVerseCheck(state) &&
    !imageList.some((x) => x.data && x.data.type === userImageType.Verse)
  ) {
    const verseList = getVerseListSelector(state);
    verseList && verseList[0] && autoSelectedSingleImage(currentImprintFormat.key, verseList[0]);
  } else if (
    type === userImageType.Sentiment &&
    currentImprintFormat.mandatorySentiment &&
    onlyOneSentimentCheck(state) &&
    !imageList.some((x) => x.data && x.data.type === userImageType.Sentiment)
  ) {
    const sentimentList = getSentimentListSelector(state);
    sentimentList && sentimentList[0] && autoSelectedSingleImage(currentImprintFormat.key, sentimentList[0]);
  }
};
